import { useEffect, useCallback, useState } from 'react';
import * as m from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { __ } from '@wordpress/i18n';
import { useBookingStore } from '../store/useBookingStore';
import {
	useService,
	useIsDateRange,
	useBookingTimeValue,
	useSelectedDate,
} from '../hooks/useBookingComputed';
import { TimezoneIndicator } from '../../shared/components/TimezoneIndicator';
import { DateRangePicker } from './DateRangePicker';
import { TimeSlotPicker } from './TimeSlotPicker';
import { ManualDateTimeInput } from './ManualDateTimeInput';
import { useGravityFormsPricing } from '../hooks/useGravityFormsPricing';
import './BookingTimePicker.css';

interface Props {
	onChange?: (value: string) => void;
	value?: string;
}

export function BookingTimePicker({ onChange, value }: Props) {
	// Detect entry edit mode
	const isEntryEdit = document.getElementById('entry_form') !== null;
	const [inputMode, setInputMode] = useState<'picker' | 'manual'>('picker');

	// Server state via React Query
	const { data: service, error: serviceError } = useService();

	// Client state via Zustand
	const selectedDate = useSelectedDate();
	const setSelectedTime = useBookingStore((state) => state.setSelectedTime);
	const showCalendar = useBookingStore((state) => state.showCalendar);
	const setShowCalendar = useBookingStore((state) => state.setShowCalendar);
	const skipAnimations = useBookingStore((state) => state.skipAnimations);
	const setSkipAnimations = useBookingStore(
		(state) => state.setSkipAnimations
	);
	const initialize = useBookingStore((state) => state.initialize);
	const isInitialized = useBookingStore((state) => state.isInitialized);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const formId = useBookingStore((state) => state.formId);
	const bookingFieldId = useBookingStore((state) => state.bookingFieldId);
	const resourceFieldIds = useBookingStore((state) => state.resourceFieldIds);
	const showTimezoneIndicator = useBookingStore(
		(state) => state.showTimezoneIndicator
	);
	const timezoneMessage = useBookingStore((state) => state.timezoneMessage);
	const setIsEntryEdit = useBookingStore((state) => state.setIsEntryEdit);

	// Computed values
	const isDateRange = useIsDateRange();
	const timeValue = useBookingTimeValue();

	// Initialize pricing integration
	const { updatePricing } = useGravityFormsPricing({
		formId,
		bookingFieldId,
		serviceId: currentServiceId,
		resourceFieldIds,
	});

	// Set isEntryEdit in store
	useEffect(() => {
		setIsEntryEdit(isEntryEdit);
	}, [isEntryEdit, setIsEntryEdit]);

	// Initialize from hidden input once when service loads
	useEffect(() => {
		if (service && value) {
			initialize(value, service);
			setSkipAnimations(true);
			// Enable animations after initial render
			requestAnimationFrame(() => {
				setSkipAnimations(false);
			});
		}
	}, [service, value, initialize, setSkipAnimations]);

	// Sync booking value to hidden input
	// Only sync after initialization to prevent race condition where
	// useBookingTimeValue computes '' and overwrites the hidden input
	// before initialize() can restore the saved state (critical for multi-page forms)
	useEffect(() => {
		if (isInitialized && onChange) {
			onChange(timeValue);
		}
	}, [isInitialized, timeValue, onChange]);

	// Pricing integration
	useEffect(() => {
		if (timeValue) {
			updatePricing(bookingFieldId, timeValue);
		}
	}, [timeValue, bookingFieldId, updatePricing]);

	// Auto-switch to time picker when date selected (non-range only)
	useEffect(() => {
		if (!isDateRange && selectedDate) {
			setShowCalendar(false);
		}
	}, [isDateRange, selectedDate, setShowCalendar]);

	const handleBackClick = useCallback(() => {
		setShowCalendar(true);
		setSelectedTime(undefined);
	}, [setShowCalendar, setSelectedTime]);

	const variants = {
		enter: (direction: number) => ({
			x: direction > 0 ? 20 : -20,
			opacity: 0,
		}),
		center: {
			x: 0,
			opacity: 1,
		},
		exit: (direction: number) => ({
			x: direction < 0 ? 20 : -20,
			opacity: 0,
		}),
	};

	const transition = skipAnimations
		? { duration: 0 }
		: {
				type: 'spring' as const,
				stiffness: 300,
				damping: 30,
			};

	// Show message if no service is selected (manual mode)
	if (currentServiceId === 0) {
		return (
			<div className="gpb-booking-time-picker__message">
				{__(
					'Please select a service to view available booking times.',
					'gp-bookings'
				)}
			</div>
		);
	}

	// Only show error after loading completes
	if (serviceError) {
		return (
			<div className="gpb-booking-time-picker__error">
				{serviceError.message}
			</div>
		);
	}

	return (
		<div className="gpb-booking-time-picker">
			{isEntryEdit && (
				<div className="gpb-booking-time-picker__mode-toggle">
					<button
						type="button"
						className={`gpb-mode-toggle-btn ${inputMode === 'picker' ? 'active' : ''}`}
						onClick={() => setInputMode('picker')}
					>
						{__('Picker', 'gp-bookings')}
					</button>
					<button
						type="button"
						className={`gpb-mode-toggle-btn ${inputMode === 'manual' ? 'active' : ''}`}
						onClick={() => setInputMode('manual')}
					>
						{__('Manual Entry', 'gp-bookings')}
					</button>
				</div>
			)}

			<div
				className={`gpb-booking-time-picker__content ${inputMode === 'manual' ? 'gpb-booking-time-picker__content--manual' : ''}`}
			>
				{inputMode === 'manual' && isEntryEdit ? (
					<ManualDateTimeInput />
				) : (
					<AnimatePresence initial={false} mode="wait">
						{showCalendar || isDateRange ? (
							<m.div
								key="calendar"
								initial={skipAnimations ? false : 'enter'}
								animate="center"
								exit="exit"
								variants={variants}
								transition={transition}
								custom={-1}
								style={{ width: '100%' }}
							>
								<DateRangePicker />
							</m.div>
						) : (
							<m.div
								key="time-slots"
								initial={skipAnimations ? false : 'enter'}
								animate="center"
								exit="exit"
								variants={variants}
								transition={transition}
								custom={1}
								style={{ width: '100%' }}
							>
								<TimeSlotPicker
									onBack={handleBackClick}
									isEntryEdit={isEntryEdit}
								/>
							</m.div>
						)}
					</AnimatePresence>
				)}
			</div>
			<TimezoneIndicator
				showIndicator={showTimezoneIndicator}
				customMessage={timezoneMessage}
			/>
		</div>
	);
}
